import { useMemo, type MouseEvent } from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@/components/ui/accordion'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Palette,
  ChevronDownIcon,
  Maximize2,
  StickyNote,
  Type,
  Layers,
  Settings2,
  RotateCcw,
  FileText,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Node } from '@xyflow/react'

interface NearestNode {
  id: string
  label: string
  zIndex: number
  distance: number
}

interface StickyNoteInspectorProps {
  data: {
    text: string
    color: string
    width: number
    height: number
    shadow: boolean
    fontSize: number
  }
  onChange: (updates: Partial<StickyNoteInspectorProps['data']>) => void
  onDelete?: () => void
  currentNode?: Node
  allNodes?: Node[]
  /** Called when drag starts (for FloatingPanel integration) */
  onDragStart?: (e: MouseEvent) => void
}

const STICKY_COLORS = {
  yellow: { light: '#FEF3C7', dark: '#78350f', name: 'Amarillo' },
  green: { light: '#D1FAE5', dark: '#14532d', name: 'Verde' },
  blue: { light: '#DBEAFE', dark: '#1e3a8a', name: 'Azul' },
  pink: { light: '#FCE7F3', dark: '#831843', name: 'Rosa' },
  purple: { light: '#EDE9FE', dark: '#581c87', name: 'Morado' },
  orange: { light: '#FED7AA', dark: '#9a3412', name: 'Naranja' },
  red: { light: '#FEE2E2', dark: '#7f1d1d', name: 'Rojo' },
  indigo: { light: '#E0E7FF', dark: '#3730a3', name: 'Índigo' },
  teal: { light: '#CCFBF1', dark: '#134e4a', name: 'Verde azulado' },
  cyan: { light: '#CFFAFE', dark: '#155e75', name: 'Cian' },
  lime: { light: '#ECFCCB', dark: '#365314', name: 'Lima' },
  emerald: { light: '#D1FAE5', dark: '#064e3b', name: 'Esmeralda' },
  gray: { light: '#F3F4F6', dark: '#374151', name: 'Gris' },
  slate: { light: '#F1F5F9', dark: '#334155', name: 'Pizarra' },
  amber: { light: '#FEF3C7', dark: '#78350f', name: 'Ámbar' },
  rose: { light: '#FFE4E6', dark: '#881337', name: 'Rosa claro' },
} as const

/** Color swatch button for the color picker */
function ColorSwatch({
  color,
  colorKey,
  name,
  selected,
  onClick,
}: {
  color: string
  colorKey: string
  name: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className={cn(
              'w-full h-8 rounded-md transition-all border-2',
              selected
                ? 'ring-2 ring-offset-1 ring-offset-background ring-primary scale-105 border-white'
                : 'hover:scale-105 border-transparent'
            )}
            style={{ backgroundColor: color }}
            aria-label={`Seleccionar color ${name}`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {name}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default function StickyNoteInspector({
  data,
  onChange,
  onDelete,
  currentNode,
  allNodes = [],
  onDragStart,
}: StickyNoteInspectorProps) {
  // Calculate the 2 nearest nodes based on distance
  const nearestNodes = useMemo(() => {
    if (!currentNode || !allNodes.length) return []

    // Calculate distance between two nodes
    const getDistance = (node1: Node, node2: Node): number => {
      const dx = node1.position.x - node2.position.x
      const dy = node1.position.y - node2.position.y
      return Math.sqrt(dx * dx + dy * dy)
    }

    // Filter out current node and calculate distances
    const nodesWithDistance = allNodes
      .filter((node) => node.id !== currentNode.id)
      .map((node) => ({
        id: node.id,
        label: (node.data as { label?: string })?.label || node.type || 'Sin nombre',
        zIndex: node.zIndex ?? 0,
        distance: getDistance(currentNode, node),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 2) // Get the 2 closest

    return nodesWithDistance as NearestNode[]
  }, [currentNode, allNodes])

  const currentZIndex = currentNode?.zIndex ?? 0

  // Reset to default values
  const handleReset = () => {
    onChange({
      color: 'yellow',
      width: 200,
      height: 150,
      fontSize: 12,
      shadow: true,
    })
  }

  // Default open sections
  const defaultOpenSections = ['color', 'content', 'size']

  return (
    <div className="pointer-events-auto nowheel" onWheelCapture={(e) => e.stopPropagation()}>
      {/* Header - draggable when onDragStart is provided */}
      <div
        className={cn(
          "p-3 border-b flex items-center justify-between",
          onDragStart && "cursor-grab active:cursor-grabbing select-none"
        )}
        onMouseDown={onDragStart}
      >
        <div>
          <h3 className="text-sm font-semibold">Propiedades de la Nota</h3>
          <p className="text-[11px] text-muted-foreground">Nota adhesiva del canvas</p>
        </div>
        <StickyNote className="h-5 w-5 text-muted-foreground" />
      </div>

      <Accordion type="multiple" className="w-full" defaultValue={defaultOpenSections}>
        {/* Section 1: Color */}
        <AccordionItem value="color" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border"
                  style={{ backgroundColor: STICKY_COLORS[(data.color ?? 'yellow') as keyof typeof STICKY_COLORS]?.light || '#FEF3C7' }}
                  aria-hidden="true"
                >
                  <Palette size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Color</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    {STICKY_COLORS[(data.color ?? 'yellow') as keyof typeof STICKY_COLORS]?.name || 'Amarillo'}
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10">
              <div className="grid grid-cols-4 gap-1.5">
                {Object.entries(STICKY_COLORS).map(([colorKey, colorValue]) => (
                  <ColorSwatch
                    key={colorKey}
                    color={colorValue.light}
                    colorKey={colorKey}
                    name={colorValue.name}
                    selected={(data.color ?? 'yellow') === colorKey}
                    onClick={() => onChange({ color: colorKey })}
                  />
                ))}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 2: Contenido */}
        <AccordionItem value="content" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <FileText size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Contenido</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    {data.text ? `${data.text.slice(0, 20)}${data.text.length > 20 ? '...' : ''}` : 'Sin contenido'}
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-2">
              <Textarea
                id="content"
                name="sticky-content"
                value={data.text ?? ''}
                onChange={(e) => onChange({ text: e.target.value })}
                placeholder="Escribe aquí... (markdown soportado)"
                className="min-h-[100px] font-mono text-xs resize-none"
                style={{ fontSize: `${Math.max(10, (data.fontSize ?? 12) - 2)}px` }}
              />
              <p className="text-[10px] text-muted-foreground">
                Soporta Markdown: **negrita**, *cursiva*, `código`
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 3: Tamaño */}
        <AccordionItem value="size" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Maximize2 size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Tamaño</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    {data.width ?? 200} × {data.height ?? 150} px
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-4">
              {/* Width */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="width" className="text-xs">
                    Ancho
                  </Label>
                  <span className="text-xs text-muted-foreground font-mono">{data.width ?? 200}px</span>
                </div>
                <Slider
                  id="width"
                  value={[data.width ?? 200]}
                  onValueChange={(value) => onChange({ width: value[0] })}
                  min={150}
                  max={600}
                  step={10}
                  className="w-full"
                />
              </div>

              {/* Height */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="height" className="text-xs">
                    Alto
                  </Label>
                  <span className="text-xs text-muted-foreground font-mono">{data.height ?? 150}px</span>
                </div>
                <Slider
                  id="height"
                  value={[data.height ?? 150]}
                  onValueChange={(value) => onChange({ height: value[0] })}
                  min={100}
                  max={400}
                  step={10}
                  className="w-full"
                />
              </div>

              {/* Font Size */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="fontSize" className="text-xs flex items-center gap-1.5">
                    <Type className="h-3 w-3" />
                    Tamaño de fuente
                  </Label>
                  <span className="text-xs text-muted-foreground font-mono">{data.fontSize ?? 12}px</span>
                </div>
                <Slider
                  id="fontSize"
                  value={[data.fontSize ?? 12]}
                  onValueChange={(value) => onChange({ fontSize: value[0] })}
                  min={8}
                  max={24}
                  step={1}
                  className="w-full"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 4: Opciones */}
        <AccordionItem value="options" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Settings2 size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Opciones</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Sombra: {(data.shadow ?? true) ? 'Activa' : 'Inactiva'}
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="shadow" className="text-xs cursor-pointer">
                  Mostrar sombra
                </Label>
                <Checkbox
                  id="shadow"
                  checked={data.shadow ?? true}
                  onCheckedChange={(checked) => onChange({ shadow: !!checked })}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 5: Capas (Z-Index) */}
        {currentNode && (
          <AccordionItem value="layers" className="py-1">
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
                <span className="flex items-center gap-2">
                  <span
                    className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                    aria-hidden="true"
                  >
                    <Layers size={14} className="opacity-60" />
                  </span>
                  <span className="flex flex-col">
                    <span>Capas</span>
                    <span className="text-[11px] font-normal text-muted-foreground">
                      z-index: {currentZIndex}
                    </span>
                  </span>
                </span>
                <ChevronDownIcon
                  size={14}
                  className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                  aria-hidden="true"
                />
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
            <AccordionContent className="px-3 pb-3">
              <div className="ml-10 space-y-2">
                <div className="flex items-center justify-between p-2 bg-accent/30 rounded-md">
                  <span className="text-xs font-medium">Esta nota:</span>
                  <span className="font-mono text-xs font-semibold">z-index: {currentZIndex}</span>
                </div>

                {nearestNodes.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium text-muted-foreground">
                      Objetos más cercanos:
                    </p>
                    {nearestNodes.map((node, idx) => (
                      <div
                        key={node.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                      >
                        <span className="text-xs truncate flex-1" title={node.label}>
                          {idx + 1}. {node.label}
                        </span>
                        <span className="font-mono text-[11px] ml-2">z: {node.zIndex}</span>
                      </div>
                    ))}
                  </div>
                )}

                {nearestNodes.length === 0 && (
                  <p className="text-[11px] text-muted-foreground italic p-2">
                    No hay otros objetos en el canvas
                  </p>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Section 6: Acciones */}
        <AccordionItem value="actions" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <RotateCcw size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Acciones</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Restablecer y eliminar
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start text-xs h-8"
                onClick={handleReset}
              >
                <RotateCcw className="h-3 w-3 mr-2" />
                Restablecer valores
              </Button>

              {onDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full justify-start text-xs h-8"
                  onClick={onDelete}
                >
                  <span className="mr-2">🗑️</span>
                  Eliminar nota
                </Button>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Footer with node ID */}
      {currentNode && (
        <div className="p-3 border-t">
          <div className="text-[10px] text-muted-foreground bg-muted px-2 py-1.5 rounded-md font-mono truncate">
            ID: {currentNode.id}
          </div>
        </div>
      )}
    </div>
  )
}
